import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useDeepEffect } from '@crossnokaye/ux-common';

import { checkSubscription } from '@/hooks/query-hooks/websockets';

export type SubscriptionIdentifier = {
  id: string;
  contextKey?: string;
};

interface UseSubscriptionBaseProps {
  enabled: boolean;
  isConnected: boolean;
  handleSendContent: (subscriptionIds: string[]) => void;
  queryKey: string[];
  subscriptionIds?: SubscriptionIdentifier[];
}

type SubscriptionBaseResult = {
  subscribe: (identifiers: SubscriptionIdentifier[]) => void;
};

/**
 * Base hook for reading settings bidirectional websocket.
 *
 * @param enabled Whether the query is enabled
 * @param isConnected Whether the websocket is connected
 * @param handleSendContent A function to send content to the websocket
 * @param queryKey The query key to use for the queries
 * @param subscriptionIds The list of IDs to subscribe to
 */
export const useSubscriptionBase = <T>({
  isConnected,
  enabled,
  handleSendContent,
  subscriptionIds,
  queryKey,
}: UseSubscriptionBaseProps): SubscriptionBaseResult => {
  const [subscribedIdentifiers, setSubscribedIdentifiers] = useState<SubscriptionIdentifier[]>(
    subscriptionIds ?? []
  );

  const queryClient = useQueryClient();

  useDeepEffect(() => {
    if (enabled && isConnected && subscribedIdentifiers.length > 0) {
      const { newSubscriptions, updated } = checkSubscription(
        queryClient,
        queryKey as string[],
        subscribedIdentifiers.map((identifiers: SubscriptionIdentifier) =>
          (queryKey as string[]).concat(identifiers.id).concat(identifiers.contextKey ?? 'default')
        )
      );
      if (updated) {
        // newSubscriptions is an array of the queryKeys for each reading point of the format [orgId, agentId, <queryKey>, refId, contextKey]
        // The websocket subscription is for each refId, so we need to extract the refId from the queryKey
        handleSendContent(
          newSubscriptions.map((subscription) => subscription[subscription.length - 2])
        );
        // Update the subscribed status for the new subscriptions
        newSubscriptions.forEach((subscriptionKey) => {
          queryClient.setQueryData(subscriptionKey, (old: T) => ({
            ...old,
            subscribed: true,
          }));
        });
      }
    }
  }, [enabled, isConnected, queryClient, queryKey, handleSendContent, subscribedIdentifiers]);

  return { subscribe: setSubscribedIdentifiers };
};
